import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';
import { Operation } from '@apollo/client/link/core/types';

import { Config } from '@root/modules/channel/types/channel';
import { GetArticleByID } from '@root/modules/article/types/article';
import getArticleByID from '@root/modules/article/graphql/getArticleByID.graphql';
import getArticleBodyByID from '@root/modules/article/graphql/getArticleBodyByID.graphql';

import { ArticleAdapter } from '../utils/ArticleAdapter';
import { dataLoader } from '@root/libs/redis/dataloader/dataLoader';

import { isValidSignedInt32 } from '@root/common/utils/helpers';

type ServiceVariables = {
  id: number | string;
  language?: string;
  preview?: string | null;
  customerToken?: string | null;
  application?: Config['settings']['application'];
  settings?: {
    requestBody: boolean;
    authorizationHeader?: boolean;
    preferHeader?: boolean;
  };
  hashedCanonical?: string;
};

type FetchVariables = GetArticleByID['variables'];

type Response = GetArticleByID['data'];

interface FetchArticleReturn {
  article: Response['article']['data'][0];
  access: boolean;
  requestContext: ReturnType<Operation['getContext']> | null;
}

export default class ArticleService extends Service implements ServiceInterface {
  private retriedFailedRequest = false;

  private getQueryWithOptions(serviceVariables: ServiceVariables) {
    const { id, preview, customerToken, settings, language, hashedCanonical } = serviceVariables;
    const variables: FetchVariables = { id: Number(id), authorLanguage: language };

    const query = settings?.requestBody ? getArticleBodyByID : getArticleByID;

    const queryWithOptions = {
      query,
      options: {
        variables,
        context: { headers: {} as Record<string, unknown> },
      },
    };

    if (preview) {
      queryWithOptions.options.context.headers.preview = preview;
      return queryWithOptions;
    }

    if (!customerToken) {
      return queryWithOptions;
    }

    if (settings?.authorizationHeader) {
      queryWithOptions.options.context.headers.Authorization = `Bearer ${customerToken}`;
    }

    if (settings?.preferHeader && hashedCanonical) {
      queryWithOptions.options.context.headers.Prefer = `auth=google-showcase, hashed-canonical-url=${hashedCanonical}`;
    }

    return queryWithOptions;
  }

  private async fetchArticle(variables: ServiceVariables, channelConfig: Config): Promise<FetchArticleReturn | undefined> {
    const { preview, application } = variables;

    if (isNaN(Number(variables.id)) || !isValidSignedInt32(Number(variables.id))) {
      this.handleError({ message: 'article.error.not_found', statusCode: 400, extraData: 'invalid input `id`' });
      return;
    }

    const { query, options: queryOptions } = this.getQueryWithOptions(variables);
    const cacheData = !preview && !application?.isSearchBot;
    try {
      const options = Object.assign({ query }, queryOptions);
      const dataLoaderOptions = {
        remote: {
          keyPrefix: 'article',
        },
      };

      let requestContext: ReturnType<Operation['getContext']> | null = null;

      const requestWrapper = async (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

        const response = await apiProvider.query<Response>(options);
        requestContext = response[1];

        return response[0].data;
      };

      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader && cacheData ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!response?.article?.data.length) {
        this.handleError({ message: 'article.error.not_found', statusCode: 404 });
        return;
      }

      const articleAdapter = new ArticleAdapter(response.article.data[0], channelConfig.page.article.component);
      const article = articleAdapter.adapt();

      return {
        article,
        access: Boolean(article.content?.paywall?.access),
        requestContext,
      };
    } catch (e) {
      // Retry failed request one time
      if (!this.retriedFailedRequest) {
        this.retriedFailedRequest = true;

        return await this.fetchArticle(variables, channelConfig);
      }

      const error = this.generateErrorData(e);
      this.handleError(error);
    }
  }

  public async fetch(variables: { variables: ServiceVariables; channelConfig: Config }) {
    const response = await this.fetchArticle(variables.variables, variables.channelConfig);

    return response;
  }
}

export function fetchArticle(variables: ServiceVariables, { req, channelConfig }: { req: PortalRootIncomingMessage; channelConfig: Config }) {
  const articleService = new ArticleService({ req });
  return articleService.fetch({ variables, channelConfig });
}
